import { isBaselineCategory, type ActionSourceBand } from "./actionSourceSegments";
import { actionSourceDotColor, actionSourceTerm } from "./actionSourceTerms";

/**
 * 영상 시간과 채널 밴드 가로축(0~1 진행도) 사이의 변환.
 * sparkline 뒤에 표시하는 구간 음영.
 *
 * 채널 밴드의 가로축은 시간이 아니라 **행 위상**이다. 행 위상은 채널마다 자기 행을 균등한
 * 칸으로 나눈 진행도다. 그래서 영상 시각을 에피소드 길이로 나눠 옮기는 것은 근사다.
 * - 행이 고르게 있는 채널에서는 거의 맞다.
 * - 늦게 켜졌거나 빈틈이 큰 채널에서는 맞지 않는다.
 * 음영은 "이 무렵 사람이 냈다"를 보여 주는 바탕일 뿐이다. 여기서 구간별 수치를 내지 않는다.
 * 위상 grid 로 자르면 수치가 틀린다.
 *
 * 색도 여기서 계산해 전달한다. 그래서 그래프 쪽은 action_source 어휘를 알 필요가 없다.
 */

export type PhaseSpan = {
  id: string;
  color: string;
  startPhase: number;
  endPhase: number;
};

/**
 * 영상 초를 가로축 진행도(0~1)로 바꾼다. 길이를 모르면 null 이다.
 * 그래서 틀린 자리에 선을 긋지 않는다.
 */
export function secondsToPhase(seconds: number, durationSeconds: number | null): number | null {
  if (durationSeconds === null || !(durationSeconds > 0) || !Number.isFinite(seconds)) {
    return null;
  }
  return Math.min(1, Math.max(0, seconds / durationSeconds));
}

/** 가로축 진행도(0~1)를 영상 초로 바꾼다. 그래프를 눌러 영상을 옮길 때 쓴다. */
export function phaseToSeconds(phase: number, durationSeconds: number): number {
  return Math.min(1, Math.max(0, phase)) * durationSeconds;
}

/**
 * sparkline 뒤 음영. 바탕(INFERENCE) 밖 띠만 표시한다.
 * 바탕까지 표시하면 그래프 전체가 덮인다.
 */
export function bandShading(
  bands: readonly ActionSourceBand[],
  durationSeconds: number | null,
): readonly PhaseSpan[] {
  const spans: PhaseSpan[] = [];
  for (const band of bands) {
    if (isBaselineCategory(band.category)) continue;
    const startPhase = secondsToPhase(band.startSeconds, durationSeconds);
    const endPhase = secondsToPhase(band.endSeconds, durationSeconds);
    if (startPhase === null || endPhase === null || endPhase <= startPhase) continue;
    spans.push({
      id: band.id,
      color: actionSourceDotColor(actionSourceTerm(band.category, band.code).dataViz),
      startPhase,
      endPhase,
    });
  }
  return spans;
}
